import { landmineNames } from '@mm/constants/landmine-names';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import React, { ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { getPlanInfo } from '@mm/state/helpers/billing-info-helpers';
import {
  EnhancedBillingPage,
  PageBillingType,
} from '@mm/state/interfaces/connected-page';
import ChatbotModel from '@mm/state/model/ChatbotModel';
import CompanyModel from '@mm/state/model/CompanyModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { tourOpenSelector } from '@mm/state/selectors/modalSelector';
import { selectSessionSelector } from '@mm/state/selectors/userSelector';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { getCurrentSubscription } from '@mm/state/selectors/subscriptionSelectors';
import {
  activeChatbotSelector,
  activeCompanySelector,
  selectActiveChatbot,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { Logo } from '../../Logo/Logo';
import styles from './NewSidebarHeader.module.css';
import { useActivateSmsRedirectCallback } from '../../../../common/hooks/use-activate-sms-redirect-callback';
import { UserRoleContainer } from '../../../../common/hooks/use-route-guard';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { trackWebAppGAEvent } from '../../../../common/helpers/plugins/website-google-analytics';
import { FreeTrialCountdown } from '../../FreeTrialCountdown/FreeTrialCountdown';
import { AllFeaturesDropdown } from '../AllFeaturesDropdown/AllFeaturesDropdown';
import SendLimitAlert from '../SendLimitAlert/SendLimitAlert';
import { PageSwitcher } from '../PageSwitcher/PageSwitcher';
import UserDropdown from '../../UserDropdown/UserDropdown';
import { TemplateGoButton } from '../../../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/TemplateGoButton';
import { AutomationTemplateContainer } from '../../../../editor/features/beta/modules/Automations/AutomationHome/TemplateIndex/AutomationTemplateContainer';
import { PlusOutlined } from '@ant-design/icons';

type Props = {
  children: ReactNode;
  facebookPage: EnhancedBillingPage;
  onMenuClick: () => void;
  setSidebarMinimized: (isMinimized: boolean) => void;
  sidebarMinimized: boolean;
  company: CompanyModel;
  activeChatbot: ChatbotModel | null;
  isFreeTrialActive?: boolean;
};

const noop = () => {};
export const useUpgradeLandmineHandler = () => {
  const activeChatbot = useSelector(selectActiveChatbot);
  const session = useSelector(selectSessionSelector);
  const activeCompany = useSelector(activeCompanySelector);
  const justCompletedOnboarding = useSelector(
    (state: StoreState) => state.onboarding.justCompletedOnboarding,
  );
  const isNowOnboarding = useSelector(tourOpenSelector);

  const usersCRUDActions = useUsersCRUDActions();
  const modalActions = useModalActions();

  const facebookPage = useSelector(activeFacebookPageSelector);

  const subscription = useSelector(getCurrentSubscription(facebookPage?.id));

  const currentSubscription = subscription || facebookPage?.subscription;

  const shouldShow = Boolean(
    activeCompany?.editable &&
      activeChatbot &&
      session?.userAttributes &&
      !session.userAttributes.seenLastPromo &&
      !currentSubscription &&
      !justCompletedOnboarding &&
      !isNowOnboarding,
  );

  const onSuccessBilling = useActivateSmsRedirectCallback();

  const callback = useCallback(() => {
    if (shouldShow && activeCompany) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['PROMOTIONS__PRO-UPGRADE'],
        planFallback: PageBillingType.Pro,
        onSuccessBilling,
      });
      usersCRUDActions.updateUserAttributes(activeCompany.id, session.id, {
        seenLastPromo: true,
        // HACK do not remove this prop
        // DOING SO WILL BREAK THE WORDPRESS PLUGIN INTEGRATION
        // AS IT RELIES ON IT BEING SET
        // first introduced in FetchBotData L:106 7165ab0ace36e449e570c2831bbefbab44787ecb
        seenNewDashboardModal: true,
      });
    }
  }, [shouldShow, activeCompany]);

  return shouldShow ? callback : noop;
};

export const NewSidebarHeader = (props: Props) => {
  const {
    facebookPage,
    onMenuClick,
    company,
    activeChatbot,
    children,
    isFreeTrialActive,
  } = props;
  const planInfo = getPlanInfo(facebookPage);
  const chatbot = useSelector(activeChatbotSelector);

  const hideSendLimitAlert = planInfo.isCustomTeam || planInfo.isLegacyTeamPlan;

  const {
    roleFlags: { isLiveChatAgent },
  } = UserRoleContainer.useContainer();

  let logoHref = '';

  if (chatbot?.id && !isLiveChatAgent) {
    logoHref = BotBuilderRoutes.dashboard.getLink({
      chatbotId: chatbot.id,
    });
  }

  const onLogoClick = () => {
    trackWebAppGAEvent('click', {
      category: 'navigation',
      label: 'navbar_logo',
    });
  };

  const showUpgradeLandmine = useUpgradeLandmineHandler();

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.wrapperWithTrial]: isFreeTrialActive,
      })}
    >
      <FreeTrialCountdown />
      <div className={styles.header} onClickCapture={showUpgradeLandmine}>
        <div className={styles.leftSide}>
          <div className={styles.logo}>
            <Logo href={logoHref} onClick={onLogoClick} />
          </div>

          {children}
          <AllFeaturesDropdown />
        </div>
        <div className={styles.rightSide}>
          {!hideSendLimitAlert && <SendLimitAlert />}
          <AutomationTemplateContainer.Provider>
            <TemplateGoButton
              label="Automation"
              leftIcon={
                <PlusOutlined className={styles.addAutomationButtonIcon} />
              }
              css={{ button: styles.addAutomationButton }}
            />
          </AutomationTemplateContainer.Provider>
          <PageSwitcher facebookPage={facebookPage} onMenuClick={onMenuClick} />
          <UserDropdown isFreeTrialActive={isFreeTrialActive} />
        </div>
      </div>
    </div>
  );
};
